import React, { useEffect, useRef, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, fileUrl, formatRupiah } from "@/lib/api";
import { Plus, Pencil, Trash2, Image as ImageIcon, X, Upload } from "lucide-react";
import { toast } from "sonner";

function Modal({ open, onClose, title, children, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white rounded-md max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold text-[#1E3A2B]">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5 text-[#5C6A61]" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function MenuManagement() {
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [optionGroups, setOptionGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);

  const [groupModal, setGroupModal] = useState({ open: false, editing: null });
  const [itemModal, setItemModal] = useState({ open: false, editing: null });

  const load = async () => {
    const [g, i, og] = await Promise.all([
      api.get("/menu/groups"),
      api.get("/menu/items"),
      api.get("/option-groups"),
    ]);
    setGroups(g.data);
    setItems(i.data);
    setOptionGroups(og.data);
    if (!activeGroup && g.data[0]) setActiveGroup(g.data[0].id);
  };

  useEffect(() => { load(); }, []);

  const filteredItems = activeGroup ? items.filter((it) => it.group_id === activeGroup) : items;

  // Group ops
  const saveGroup = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = { name: fd.get("name"), sort_order: Number(fd.get("sort_order") || 0) };
    if (groupModal.editing) {
      await api.put(`/menu/groups/${groupModal.editing.id}`, payload);
      toast.success("Grup diperbarui");
    } else {
      await api.post("/menu/groups", payload);
      toast.success("Grup ditambahkan");
    }
    setGroupModal({ open: false, editing: null });
    load();
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Hapus grup "${g.name}"? Semua menu di dalamnya akan ikut terhapus.`)) return;
    await api.delete(`/menu/groups/${g.id}`);
    toast.success("Grup dihapus");
    load();
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Hapus menu "${it.name}"?`)) return;
    await api.delete(`/menu/items/${it.id}`);
    toast.success("Menu dihapus");
    load();
  };

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Menu</h1>
          <p className="text-[#5C6A61] mt-1">Kelola grup menu dan item menu Anda</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="btn-add-group"
            onClick={() => setGroupModal({ open: true, editing: null })}
            className="bg-[#EAE7DF] text-[#1E3A2B] hover:bg-[#D3CEC4] px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Grup
          </button>
          <button
            data-testid="btn-add-item"
            disabled={groups.length === 0}
            onClick={() => setItemModal({ open: true, editing: null })}
            className="bg-[#C25934] text-white hover:bg-[#A94726] px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Menu
          </button>
        </div>
      </div>

      {/* Group tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {groups.length === 0 && <div className="text-sm text-[#5C6A61]">Belum ada grup menu. Buat grup terlebih dahulu.</div>}
        {groups.map((g) => (
          <div key={g.id} className={`inline-flex items-center gap-1 rounded-full border ${activeGroup === g.id ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white text-[#1E3A2B] border-[#D3CEC4]"}`}>
            <button
              onClick={() => setActiveGroup(g.id)}
              data-testid={`tab-group-${g.id}`}
              className="pl-4 pr-2 py-2 text-sm"
            >
              {g.name}
            </button>
            <button
              onClick={() => setGroupModal({ open: true, editing: g })}
              data-testid={`btn-edit-group-${g.id}`}
              className="p-1.5 hover:opacity-70"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => deleteGroup(g)}
              data-testid={`btn-delete-group-${g.id}`}
              className="p-1.5 pr-3 hover:opacity-70"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Items grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="items-grid">
        {filteredItems.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#5C6A61] border border-dashed border-[#D3CEC4] rounded-md">
            Belum ada menu di grup ini.
          </div>
        )}
        {filteredItems.map((it) => (
          <div key={it.id} className="bg-white rounded-md border border-[#D3CEC4] overflow-hidden">
            <div className="aspect-video bg-[#EAE7DF] flex items-center justify-center">
              {it.image_path ? (
                <img src={fileUrl(it.image_path)} alt={it.name} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-10 h-10 text-[#5C6A61]/40" />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-[#1E3A2B]">{it.name}</div>
                  <div className="text-xs text-[#5C6A61] line-clamp-2">{it.description}</div>
                </div>
                <div className="font-display font-semibold text-[#C25934]">{formatRupiah(it.price)}</div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EAE7DF]">
                <span className={`text-xs px-2 py-1 rounded-full ${it.available ? "bg-[#3A7D44]/10 text-[#3A7D44]" : "bg-[#D64045]/10 text-[#D64045]"}`}>
                  {it.available ? "Tersedia" : "Habis"}
                </span>
                <div className="flex gap-1">
                  <button data-testid={`btn-edit-item-${it.id}`} onClick={() => setItemModal({ open: true, editing: it })} className="p-2 hover:bg-[#F9F9F6] rounded-md"><Pencil className="w-4 h-4 text-[#5C6A61]" /></button>
                  <button data-testid={`btn-delete-item-${it.id}`} onClick={() => deleteItem(it)} className="p-2 hover:bg-[#F9F9F6] rounded-md"><Trash2 className="w-4 h-4 text-[#D64045]" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Group modal */}
      <Modal open={groupModal.open} onClose={() => setGroupModal({ open: false, editing: null })} title={groupModal.editing ? "Edit Grup" : "Tambah Grup"} testid="modal-group">
        <form onSubmit={saveGroup} className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nama Grup</span>
            <input data-testid="input-group-name" name="name" defaultValue={groupModal.editing?.name || ""} required className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Urutan</span>
            <input data-testid="input-group-order" name="sort_order" type="number" defaultValue={groupModal.editing?.sort_order ?? 0} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
          </label>
          <button data-testid="btn-save-group" type="submit" className="w-full bg-[#C25934] text-white py-2.5 rounded-xl font-medium">Simpan</button>
        </form>
      </Modal>

      {/* Item modal */}
      <ItemFormModal
        open={itemModal.open}
        onClose={() => setItemModal({ open: false, editing: null })}
        editing={itemModal.editing}
        groups={groups}
        optionGroups={optionGroups}
        onSaved={() => { setItemModal({ open: false, editing: null }); load(); }}
      />
    </OwnerLayout>
  );
}

function ItemFormModal({ open, onClose, editing, groups, optionGroups, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [groupId, setGroupId] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [available, setAvailable] = useState(true);
  const [selectedOptGroups, setSelectedOptGroups] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setDescription(editing?.description || "");
      setPrice(editing?.price?.toString() || "");
      setGroupId(editing?.group_id || groups[0]?.id || "");
      setImagePath(editing?.image_path || null);
      setAvailable(editing?.available ?? true);
      setSelectedOptGroups(editing?.option_group_ids || []);
    }
  }, [open, editing, groups]);

  if (!open) return null;

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd);
      setImagePath(data.path);
      toast.success("Foto diunggah");
    } catch {
      toast.error("Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name, description, price: Number(price), group_id: groupId,
      image_path: imagePath, available, option_group_ids: selectedOptGroups,
    };
    try {
      if (editing) await api.put(`/menu/items/${editing.id}`, payload);
      else await api.post("/menu/items", payload);
      toast.success("Menu disimpan");
      onSaved();
    } catch {
      toast.error("Gagal menyimpan");
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit Menu" : "Tambah Menu"} testid="modal-item">
      <form onSubmit={save} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Nama Menu</span>
          <input data-testid="input-item-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Deskripsi</span>
          <textarea data-testid="input-item-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Harga (Rp)</span>
            <input data-testid="input-item-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} required className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Grup</span>
            <select data-testid="input-item-group" value={groupId} onChange={(e) => setGroupId(e.target.value)} required className="mt-1 w-full border border-[#D3CEC4] rounded-xl px-3 py-2.5">
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
        </div>

        <div>
          <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Foto Menu</span>
          <div className="mt-1 flex items-center gap-3">
            <div className="w-24 h-24 rounded-md bg-[#EAE7DF] flex items-center justify-center overflow-hidden border border-[#D3CEC4]">
              {imagePath ? <img src={fileUrl(imagePath)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#5C6A61]/40" />}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="input-item-image" />
              <button type="button" onClick={() => fileRef.current?.click()} className="bg-[#EAE7DF] text-[#1E3A2B] px-3 py-2 rounded-xl text-sm inline-flex items-center gap-2" data-testid="btn-upload-image">
                <Upload className="w-4 h-4" /> {uploading ? "Mengunggah..." : "Unggah Foto"}
              </button>
              {imagePath && <button type="button" onClick={() => setImagePath(null)} className="ml-2 text-xs text-[#D64045]">Hapus</button>}
            </div>
          </div>
        </div>

        {optionGroups.length > 0 && (
          <div>
            <span className="text-xs uppercase tracking-widest font-bold text-[#5C6A61]">Grup Opsi</span>
            <div className="mt-1 space-y-1.5">
              {optionGroups.map((og) => (
                <label key={og.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid={`chk-optgroup-${og.id}`}
                    checked={selectedOptGroups.includes(og.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedOptGroups([...selectedOptGroups, og.id]);
                      else setSelectedOptGroups(selectedOptGroups.filter((x) => x !== og.id));
                    }}
                  />
                  {og.name} <span className="text-xs text-[#5C6A61]">({og.options.length} opsi)</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input data-testid="chk-item-available" type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} />
          Tersedia untuk dipesan
        </label>

        <button data-testid="btn-save-item" type="submit" className="w-full bg-[#C25934] text-white py-2.5 rounded-xl font-medium">Simpan Menu</button>
      </form>
    </Modal>
  );
}
